import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { getProjectById, getProjectDisplayName, type Project } from '@data/projects';
import { getPlanById, type Plan } from '@services/plans';
import { getOfferById } from '@services/offers';
import type { OfferResponse } from '@services/offers';
import { getImageUrl } from '@utils/imageUrl';
import BackButton from '../../shared/BackButton';
import ConversationThread from '@components/ConversationThread';
import { useAuth } from '@services/auth';
import { useAdminViewAs, getSeekerPlanActorUserId } from '../../lib/adminViewAs';
import { timelineSummaryFromGanttTasks } from '@utils/ganttDataGenerator';
import type { GanttTask } from '../../types/gantt';
import TaskDetailModal from '../../components/TaskDetailModal';
import ImageModal from '../../shared/ImageModal';
import * as tasksAPI from '@services/tasks';
import type { TaskResponse } from '@services/tasks';
import { generateProjectPDF } from '@utils/pdfGenerator';
import { getComponentLabel } from '@data/componentLabels';
import { useTranslatedAIContent } from '../../shared/useTranslatedAIContent';
import { useI18n } from '../../i18n';
import { normalizePricingBreakdown } from '@utils/pricingBreakdownUtils';

// Convert backend task to GanttTask format (matches Calendar/Tasks pages)
function dbTaskToGanttTask(dbTask: TaskResponse, project: Project): GanttTask {
	return {
		id: `db-${dbTask.id}`,
		name: dbTask.title,
		description: dbTask.description || undefined,
		startDate: dbTask.start_date ? new Date(dbTask.start_date).toISOString() : new Date().toISOString(),
		endDate: dbTask.end_date ? new Date(dbTask.end_date).toISOString() : new Date().toISOString(),
		duration: dbTask.duration || 1,
		progress: dbTask.progress || 0,
		status: dbTask.status || 'not_started',
		dependencies: dbTask.dependencies || [],
		isCritical: dbTask.is_critical || false,
		gardenerIds: dbTask.gardener_ids.map(id => String(id)),
		gardenerNames: dbTask.gardener_names,
		machineIds: dbTask.machine_ids || [],
		projectId: project.id,
		notes: dbTask.notes || undefined,
		legalSeasonalBanCandidate: Boolean(dbTask.legal_seasonal_ban_candidate),
		legalSeasonalBanReason: dbTask.legal_seasonal_ban_reason || undefined
	} as GanttTask;
}

export default function SeekerProjectDetail() {
	const { projectId } = useParams<{ projectId: string }>();
	const { user } = useAuth();
	const { viewAsTarget } = useAdminViewAs();
	const [project, setProject] = useState<Project | undefined>(undefined);
	const [plan, setPlan] = useState<Plan | null>(null);
	const [offer, setOffer] = useState<OfferResponse | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
	const [dbTasks, setDbTasks] = useState<TaskResponse[]>([]);
	const [selectedTaskForModal, setSelectedTaskForModal] = useState<{ task: GanttTask; project: Project } | null>(
		null
	);
	const [imageModalOpen, setImageModalOpen] = useState(false);
	const [imageModalIndex, setImageModalIndex] = useState(0);
	const [seekerTab, setSeekerTab] = useState<'plan' | 'messages' | 'timeline' | 'questionnaire'>('plan');
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const locale = language === 'de' ? 'de-DE' : language === 'hr' ? 'hr-HR' : 'en-GB';

	useEffect(() => {
		if (!projectId) return;
		setIsLoading(true);
		getProjectById(projectId)
			.then((p) => {
				setProject(p);
				if (!p) return;
				const planId = p.planId ? parseInt(String(p.planId), 10) : NaN;
				if (!isNaN(planId)) {
					getPlanById(planId)
						.then(setPlan)
						.catch(() => setPlan(null));
				}
			})
			.catch(() => setProject(undefined))
			.finally(() => setIsLoading(false));
	}, [projectId]);

	useEffect(() => {
		if (!project?.offerId) {
			setOffer(null);
			return;
		}
		const offerId = parseInt(String(project.offerId), 10);
		if (Number.isNaN(offerId)) {
			setOffer(null);
			return;
		}
		let cancelled = false;
		const seekerActor = getSeekerPlanActorUserId(user, viewAsTarget, { planOwnerUserId: plan?.user_id });
		if (seekerActor == null) {
			setOffer(null);
			return;
		}
		getOfferById(offerId, { seekerId: seekerActor })
			.then((o) => {
				if (!cancelled) setOffer(o);
			})
			.catch(() => {
				if (!cancelled) setOffer(null);
			});
		return () => {
			cancelled = true;
		};
	}, [project?.offerId, user, viewAsTarget, plan?.user_id]);

	// Load backend tasks for this project so seeker timeline matches Calendar and supports notes/attachments
	useEffect(() => {
		if (!project?.id) {
			setDbTasks([]);
			return;
		}
		const projectIdNum =
			typeof project.id === 'string' ? parseInt(project.id, 10) : (project.id as number | undefined);
		if (!projectIdNum || Number.isNaN(projectIdNum)) {
			setDbTasks([]);
			return;
		}
		let cancelled = false;
		tasksAPI
			.getTasksForProject(projectIdNum)
			.then(tasks => {
				if (!cancelled) setDbTasks(tasks || []);
			})
			.catch(() => {
				if (!cancelled) setDbTasks([]);
			});
		return () => {
			cancelled = true;
		};
	}, [project?.id]);

	const statusLabel =
		project?.status === 'in_progress'
			? copy('In progress', 'In Bearbeitung', 'U tijeku')
			: project?.status === 'planning'
				? copy('Planning', 'Planung', 'Planiranje')
				: project?.status === 'quoted'
					? copy('Quoted', 'Angeboten', 'Ponuda poslana')
					: project?.status === 'completed'
						? copy('Completed', 'Abgeschlossen', 'Završeno')
						: project?.status === 'cancelled'
							? copy('Cancelled', 'Storniert', 'Otkazano')
							: project?.status ?? '';
	const projectCompanyDisplay = project?.companyName || project?.gardenerName || '';

	// Hooks must run before any conditional return (rules of hooks)
	// Prefer backend tasks (synced with Calendar); fall back to project/offer timeline when none
	const normalizedTasks = useMemo(() => {
		let sourceTasks: GanttTask[];

		if (project && dbTasks.length > 0) {
			sourceTasks = dbTasks.map(t => dbTaskToGanttTask(t, project));
		} else {
			const raw = (project?.ganttData ?? (offer as any)?.timeline_data ?? []) as any[];
			sourceTasks = raw.map((t): GanttTask => ({
				...t,
				id: t.id ?? t.name,
				name: t.name ?? t.title ?? 'Task',
				startDate: t.startDate ?? t.start_date ?? new Date().toISOString(),
				endDate: t.endDate ?? t.end_date ?? new Date().toISOString(),
				duration: t.duration ?? 1,
				progress: t.progress ?? 0,
				status: t.status ?? 'not_started'
			}));
		}

		// Sort by start date so the seeker sees a clear chronological flow
		return sourceTasks.sort(
			(a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
		);
	}, [project, dbTasks, project?.ganttData, offer]);
	const timelineSummary = useMemo(() => timelineSummaryFromGanttTasks(normalizedTasks), [normalizedTasks]);
	const completedCount = normalizedTasks.filter((t) => (t.progress ?? 0) >= 100 || t.status === 'completed').length;
	const totalTasks = normalizedTasks.length;
	const progressPct = totalTasks ? Math.round((completedCount / totalTasks) * 100) : 0;
	const hasTimeline = totalTasks > 0;

	const getSeekerTabStyle = (tab: 'plan' | 'messages' | 'timeline' | 'questionnaire') => ({
		color: seekerTab === tab ? '#10b981' : '#6c757d',
		borderBottom: seekerTab === tab ? '3px solid #10b981' : '3px solid transparent',
		fontWeight: seekerTab === tab ? 600 : 400,
		backgroundColor: 'transparent',
		borderTop: 'none',
		borderLeft: 'none',
		borderRight: 'none',
		transition: 'all 0.2s ease'
	});

	const seekerSmartQuestionRows = useMemo(() => {
		if (!plan) return [];

		// Backend stores ai_questions_json / ai_answers_json as JSON strings in the DB.
		// The frontend `Plan` type doesn't include them, so we treat the plan as `any` here.
		const anyPlan = plan as any;
		const decodeJsonMaybe = (val: unknown): any | undefined => {
			if (val == null) return undefined;
			if (typeof val === 'string') {
				const trimmed = val.trim();
				if (!trimmed) return undefined;
				try {
					return JSON.parse(trimmed);
				} catch {
					return undefined;
				}
			}
			if (typeof val === 'object') return val;
			return undefined;
		};

		const aiQuestionsDecoded = decodeJsonMaybe(anyPlan.ai_questions_json ?? anyPlan.ai_questions);
		const aiAnswersDecoded = decodeJsonMaybe(anyPlan.ai_answers_json ?? anyPlan.ai_answers);

		const aiQuestions = Array.isArray(aiQuestionsDecoded) ? aiQuestionsDecoded : [];
		if (!aiAnswersDecoded || typeof aiAnswersDecoded !== 'object' || Array.isArray(aiAnswersDecoded)) return [];

		const answersMap: Record<string, any> = {};
		const commentsMap: Record<string, string> = {};
		for (const [k, v] of Object.entries(aiAnswersDecoded as Record<string, any>)) {
			const match = k.match(/^ai_q_(\d+)_comment$/);
			if (match) {
				commentsMap[match[1]] = v == null ? '' : String(v);
			} else {
				answersMap[k] = v;
			}
		}

		const answerKeys = Object.keys(answersMap).filter((k) => /^ai_q_\d+$/.test(k));
		const validAnswerKeys = answerKeys.filter((k) => {
			const v = answersMap[k];
			if (v == null) return false;
			if (typeof v === 'string') return v.trim().length > 0;
			if (Array.isArray(v)) return v.some((x) => String(x ?? '').trim().length > 0);
			return true;
		});

		const indices = new Set<number>();
		for (const k of validAnswerKeys) {
			const idx = Number(k.match(/^ai_q_(\d+)$/)?.[1]);
			if (!Number.isNaN(idx)) indices.add(idx);
		}
		for (const k of Object.keys(commentsMap)) {
			const idx = Number(k);
			if (!Number.isNaN(idx) && commentsMap[k]?.trim().length) indices.add(idx);
		}

		const sorted = Array.from(indices).sort((a, b) => a - b);
		if (sorted.length === 0) return [];

		const formatAnswer = (val: any): string => {
			if (val == null) return '';
			if (Array.isArray(val)) return val.filter((x) => String(x ?? '').trim().length > 0).map(String).join(', ');
			if (typeof val === 'boolean') return val ? 'Yes' : 'No';
			const str = String(val);
			if (str === 'yes') return 'Yes';
			if (str === 'no') return 'No';
			return str;
		};

			return sorted.map((idx) => {
			const answerKey = `ai_q_${idx}`;
			const answerVal = answersMap[answerKey];
			const commentVal = commentsMap[String(idx)];
			const qText = aiQuestions?.[idx]?.question;
			return {
				idx,
					questionText: qText?.trim().length ? qText : 'Smart question',
				answerText: formatAnswer(answerVal),
				commentText: commentVal?.trim().length ? commentVal : undefined
			};
		}).filter((row) => row.answerText.trim().length > 0 || (row.commentText && row.commentText.trim().length > 0));
	}, [plan]);

	// Keep hooks before any conditional return (avoid hook-order runtime errors).
	const snapshot = offer?.plan_snapshot as any;
	// Same as OffersForMyPlan: snapshot can have aiGenerated nested or flat content
	const aiContent = snapshot?.aiGenerated ?? snapshot;
	const hasSnapshot = aiContent && (aiContent.planDescription || (aiContent.keyFeatures && aiContent.keyFeatures.length) || aiContent.instructions?.length);
	const displayedPlanBase = hasSnapshot ? aiContent : (plan?.ai_plan_details ?? null);
	const { translatedContent: displayedPlanTranslated } = useTranslatedAIContent(displayedPlanBase ?? null);
	const displayedPlan = displayedPlanTranslated ?? displayedPlanBase;
	const planPricing = useMemo(
		() =>
			displayedPlan?.pricingBreakdown
				? normalizePricingBreakdown(displayedPlan.pricingBreakdown)
				: undefined,
		[displayedPlan?.pricingBreakdown]
	);
	const planTitle = plan?.title ?? project?.name ?? 'Plan';
	const planStyle = plan?.style ?? aiContent?.style ?? null;
	const localizeStyleLabel = (styleKey?: string | null) => {
		const key = String(styleKey ?? '').toLowerCase();
		switch (key) {
			case 'modern': return copy('Modern', 'Modern', 'Moderni');
			case 'mediterranean': return copy('Mediterranean', 'Mediterran', 'Mediteranski');
			case 'minimalist': return copy('Minimalist', 'Minimalistisch', 'Minimalistički');
			case 'cottage': return copy('Cottage', 'Landhaus', 'Vikendica');
			case 'japanese': return copy('Japanese Zen', 'Japanischer Zen', 'Japanski Zen');
			case 'tropical': return copy('Tropical', 'Tropisch', 'Tropski');
			case 'rustic': return copy('Rustic', 'Rustikal', 'Rustikalni');
			case 'coastal': return copy('Coastal', 'Küstenstil', 'Obalni');
			case 'scandinavian': return copy('Scandinavian', 'Skandinavisch', 'Skandinavski');
			case 'traditional': return copy('Traditional', 'Traditionell', 'Tradicionalni');
			default: return String(styleKey ?? '');
		}
	};
	const localizeTagLabel = (tag: string) => {
		const normalized = String(tag ?? '').trim().toLowerCase();
		return localizeStyleLabel(normalized) || tag;
	};
	const planLocation = plan?.location ?? null;
	const planGardenSize = plan?.garden_size ?? null;
	const planNeedsWants = plan?.needs_wants ?? null;

	const beforeImageUrl = plan?.before_image_url ? getImageUrl(plan.before_image_url) || plan.before_image_url : null;
	const afterImageUrl = (aiContent?.photoAfterUrl && !String(aiContent.photoAfterUrl).includes('unsplash.com'))
		? (getImageUrl(aiContent.photoAfterUrl) || aiContent.photoAfterUrl)
		: (plan?.after_image_url && !plan.after_image_url.includes('unsplash.com'))
			? (getImageUrl(plan.after_image_url) || plan.after_image_url)
			: null;
	const placeholderImage = 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800&h=400&fit=crop';

	if (isLoading) {
		return (
			<div className="container-fluid py-4">
				<BackButton fallbackPath="/app/seeker/projects" label={copy('Back to Projects', 'Zurück zu Projekten', 'Natrag na projekte')} />
				<div className="text-center py-5">
					<div className="spinner-border text-success" role="status">
						<span className="visually-hidden">{copy('Loading project...', 'Projekt wird geladen...', 'Učitavanje projekta...')}</span>
					</div>
				</div>
			</div>
		);
	}

	if (!project) {
		return (
			<div className="container-fluid py-4">
				<BackButton fallbackPath="/app/seeker/projects" label={copy('Back to Projects', 'Zurück zu Projekten', 'Natrag na projekte')} />
				<div className="alert alert-warning">{copy('Project not found.', 'Projekt nicht gefunden.', 'Projekt nije pronađen.')}</div>
			</div>
		);
	}

	async function handleDownloadProjectPDF() {
		if (!aiContent || !normalizedTasks || normalizedTasks.length === 0) {
			window.alert('Project PDF is not available yet (missing plan timeline/tasks).');
			return;
		}
		if (!project) {
			window.alert('Project not found.');
			return;
		}

		try {
			setIsDownloadingPdf(true);

			const offerMeta = offer
				? {
					bands: {
						min: offer.price_min ?? 0,
						mid: offer.price_mid ?? offer.price_final ?? offer.price_min ?? 0,
						max: offer.price_max ?? offer.price_final ?? offer.price_min ?? 0
					},
					message: offer.public_notes || offer.proposal_text || offer.proposal_summary || undefined,
					validUntil: offer.valid_until,
					projectStartDate: offer.project_start_date,
					timelineEstimate: offer.estimated_timeline
				}
				: undefined;

			await generateProjectPDF({
				projectTitle: getProjectDisplayName(project),
				projectStatusLabel: statusLabel,
				gardenVision: aiContent as any,
				ganttTasks: normalizedTasks,
				offerMeta,
				client: project.client,
				companyName: projectCompanyDisplay || undefined,
				budget: project.budget,
				dueDate: project.dueDate,
				beforeImageUrl,
				afterImageUrl,
				detailLevel: 'seeker',
				language
			});
		} catch (err) {
			console.error('Failed to generate project PDF:', err);
			window.alert('Failed to generate project PDF. Please try again.');
		} finally {
			setIsDownloadingPdf(false);
		}
	}

	return (
		<div className="container-fluid py-4 px-3 px-md-4">
			<BackButton fallbackPath="/app/seeker/projects" label={copy('Back to Projects', 'Zurück zu Projekten', 'Natrag na projekte')} />

			{/* Before & After images */}
			<div className="row g-3 mb-4">
				<div className="col-12 col-md-6">
					<div className="mb-1 small text-muted">{copy('Before', 'Vorher', 'Prije')}</div>
					<div
						className="rounded-3 overflow-hidden shadow-sm bg-secondary"
						style={{ height: 220, cursor: 'pointer' }}
						role="button"
						tabIndex={0}
						onClick={() => {
							setImageModalIndex(0);
							setImageModalOpen(true);
						}}
						onKeyDown={e => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								setImageModalIndex(0);
								setImageModalOpen(true);
							}
						}}
						aria-label={copy('View before image full size', 'Vorher-Bild in voller Größe anzeigen', 'Prikaži fotografiju prije u punoj veličini')}
					>
						<img
							src={beforeImageUrl || placeholderImage}
							alt={copy('Before', 'Vorher', 'Prije')}
							className="w-100 h-100 object-fit-cover"
						/>
					</div>
				</div>
				<div className="col-12 col-md-6">
					<div className="mb-1 small text-muted">{copy('After (proposed)', 'Nachher (Vorschlag)', 'Nakon (predloženo)')}</div>
					<div
						className="rounded-3 overflow-hidden shadow-sm bg-secondary"
						style={{ height: 220, cursor: 'pointer' }}
						role="button"
						tabIndex={0}
						onClick={() => {
							const hasBefore = !!beforeImageUrl;
							setImageModalIndex(hasBefore ? 1 : 0);
							setImageModalOpen(true);
						}}
						onKeyDown={e => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								const hasBefore = !!beforeImageUrl;
								setImageModalIndex(hasBefore ? 1 : 0);
								setImageModalOpen(true);
							}
						}}
						aria-label={copy('View after image full size', 'Nachher-Bild in voller Größe anzeigen', 'Prikaži fotografiju poslije u punoj veličini')}
					>
						<img
							src={afterImageUrl || placeholderImage}
							alt={copy('After (proposed)', 'Nachher (Vorschlag)', 'Nakon (predloženo)')}
							className="w-100 h-100 object-fit-cover"
						/>
					</div>
				</div>
			</div>

			<div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3 mb-4 pb-3 border-bottom border-2 border-success border-opacity-25">
				<div>
					<h1 className="h3 mb-1 fw-bold text-dark">{getProjectDisplayName(project)}</h1>
					{projectCompanyDisplay && (
						<p className="text-muted mb-0 small">
							<i className="bi bi-building me-1"></i>{copy('Company:', 'Firma:', 'Tvrtka:')} {projectCompanyDisplay}
						</p>
					)}
				</div>
				<div className="d-flex gap-2 align-items-center flex-wrap justify-content-end">
					<span
						className={`badge ${
							project.status === 'completed'
								? 'text-bg-success'
								: project.status === 'in_progress'
									? 'text-bg-primary'
									: project.status === 'quoted'
										? 'text-bg-info'
										: 'text-bg-secondary'
						}`}
					>
						{statusLabel}
					</span>
					<button
						type="button"
						className="btn btn-outline-success btn-sm"
						onClick={handleDownloadProjectPDF}
						disabled={isDownloadingPdf || !hasSnapshot || normalizedTasks.length === 0}
						title={hasSnapshot && normalizedTasks.length > 0 ? copy('Download project PDF', 'Projekt-PDF herunterladen', 'Preuzmi projektni PDF') : copy('Project PDF not available yet', 'Projekt-PDF ist noch nicht verfügbar', 'Projektni PDF još nije dostupan')}
					>
						<i className="bi bi-download me-2"></i>
						{copy('Download PDF', 'PDF herunterladen', 'Preuzmi PDF')}
					</button>
				</div>
			</div>

			{/* Seeker tabs */}
			<div className="card mb-4">
				<div className="card-body p-0">
					<ul className="nav nav-tabs" role="tablist" style={{ borderBottomColor: '#a7f3d0', borderBottomWidth: '2px' }}>
						<li className="nav-item" role="presentation">
							<button
								className="nav-link"
								style={getSeekerTabStyle('plan')}
								onClick={() => setSeekerTab('plan')}
								type="button"
								role="tab"
							>
								{copy('Plan', 'Plan', 'Plan')}
							</button>
						</li>
						<li className="nav-item" role="presentation">
							<button
								className="nav-link"
								style={getSeekerTabStyle('messages')}
								onClick={() => setSeekerTab('messages')}
								type="button"
								role="tab"
							>
								{copy('Messages', 'Nachrichten', 'Poruke')}
							</button>
						</li>
						<li className="nav-item" role="presentation">
							<button
								className="nav-link"
								style={getSeekerTabStyle('timeline')}
								onClick={() => setSeekerTab('timeline')}
								type="button"
								role="tab"
							>
								{copy('Timeline', 'Zeitplan', 'Vremenski plan')}
							</button>
						</li>
						<li className="nav-item" role="presentation">
							<button
								className="nav-link"
								style={getSeekerTabStyle('questionnaire')}
								onClick={() => setSeekerTab('questionnaire')}
								type="button"
								role="tab"
							>
								{copy('Client Inputs', 'Kundeneingaben', 'Unos klijenta')}
							</button>
						</li>
					</ul>
				</div>
			</div>

			<div className="row g-4">
				<div className="col-12 col-md-6">
					<div className="card shadow-sm">
						<div className="card-body">
							<h5 className="card-title text-success">{copy('Project details', 'Projektdetails', 'Detalji projekta')}</h5>
							<dl className="row mb-0">
								<dt className="col-sm-4">{copy('Status', 'Status', 'Status')}</dt>
								<dd className="col-sm-8">{statusLabel}</dd>
								<dt className="col-sm-4">{copy('Budget', 'Budget', 'Budžet')}</dt>
								<dd className="col-sm-8">{project.budget?.toLocaleString() ?? '0'} €</dd>
								{project.dueDate && (
									<>
										<dt className="col-sm-4">{copy('Due date', 'Fälligkeitsdatum', 'Datum dospijeća')}</dt>
										<dd className="col-sm-8">{new Date(project.dueDate).toLocaleDateString(locale)}</dd>
									</>
								)}
							</dl>
						</div>
					</div>
				</div>
				<div className="col-12 col-md-6">
					<div className="card shadow-sm">
						<div className="card-body">
							<h5 className="card-title text-success">{copy('Tags', 'Schlagwörter', 'Oznake')}</h5>
							<div className="d-flex flex-wrap gap-2">
								{project.tags?.length
									? project.tags.map((tag) => (
											<span key={tag} className="badge text-bg-light text-muted">
												{localizeTagLabel(tag)}
											</span>
										))
									: '—'}
							</div>
						</div>
					</div>
				</div>

				{/* Messages with gardener */}
				{seekerTab === 'messages' && projectId && user && (() => {
					const projectIdNum = parseInt(projectId, 10);
					const userIdNum = getSeekerPlanActorUserId(user, viewAsTarget, { planOwnerUserId: plan?.user_id });
					if (isNaN(projectIdNum) || userIdNum == null || isNaN(userIdNum)) return null;
					return (
						<div className="col-12">
							<div className="card shadow-sm">
								<div className="card-body">
									<ConversationThread
										variant="project"
										projectId={projectIdNum}
										currentUserId={userIdNum}
										title={
											projectCompanyDisplay
												? copy(`Conversation with ${projectCompanyDisplay}`, `Unterhaltung mit ${projectCompanyDisplay}`, `Razgovor s ${projectCompanyDisplay}`)
												: copy('Messages', 'Nachrichten', 'Poruke')
										}
									/>
								</div>
							</div>
						</div>
					);
				})()}

				{/* Plan: same layout as plan detail (overview, features, instructions, tools, pricing). Use gardener-edited snapshot when present. */}
				{seekerTab === 'plan' && (plan || displayedPlan || snapshot) && (
					<div className="col-12">
						<h5 className="text-success mb-3">
							{hasSnapshot
								? copy('Plan (as quoted by gardener)', 'Plan (wie vom Gärtner angeboten)', 'Plan (kako ga je ponudio vrtlar)')
								: copy('Your plan', 'Ihr Plan', 'Vaš plan')}
						</h5>

						{/* Plan Overview */}
						<div className="card mb-4">
							<div className="card-header bg-transparent border-success border-bottom">
								<h6 className="mb-0">{copy('Plan overview', 'Planübersicht', 'Pregled plana')}</h6>
							</div>
							<div className="card-body">
								<div className="mb-3">
									<div className="text-muted small">{copy('Title', 'Titel', 'Naslov')}</div>
									<div className="fw-bold">{planTitle}</div>
								</div>
								{planStyle && (
									<div className="mb-3">
										<div className="text-muted small">{copy('Style', 'Stil', 'Stil')}</div>
										<div className="fw-bold text-capitalize">{localizeStyleLabel(planStyle)}</div>
									</div>
								)}
								{planGardenSize != null && (
									<div className="mb-3">
										<div className="text-muted small">{copy('Garden size', 'Gartengröße', 'Veličina vrta')}</div>
										<div className="fw-bold">{Number(planGardenSize)} m²</div>
									</div>
								)}
								{planLocation && (
									<div className="mb-3">
										<div className="text-muted small">{copy('Location', 'Standort', 'Lokacija')}</div>
										<div className="fw-bold">{planLocation}</div>
									</div>
								)}
								{(planNeedsWants || displayedPlan?.planDescription) && (
									<div className="mb-0">
										<div className="text-muted small mb-1">{copy('Description', 'Beschreibung', 'Opis')}</div>
										<p className="mb-0 text-break">{planNeedsWants || displayedPlan?.planDescription}</p>
									</div>
								)}
							</div>
						</div>

						{displayedPlan?.keyFeatures && Array.isArray(displayedPlan.keyFeatures) && displayedPlan.keyFeatures.length > 0 && (
							<div className="card mb-4">
								<div className="card-header bg-transparent border-success border-bottom">
									<h6 className="mb-0">{copy('Key features', 'Hauptmerkmale', 'Ključne značajke')}</h6>
								</div>
								<div className="card-body">
									<ul className="list-unstyled mb-0">
										{displayedPlan.keyFeatures.map((f: string, i: number) => (
											<li key={i} className="mb-2">
												<i className="bi bi-check-circle-fill text-success me-2"></i>
												{f}
											</li>
										))}
									</ul>
								</div>
							</div>
						)}

						{displayedPlan?.instructions && Array.isArray(displayedPlan.instructions) && displayedPlan.instructions.length > 0 && (
							<div className="card mb-4">
								<div className="card-header bg-transparent border-success border-bottom">
									<h6 className="mb-0">{copy('Implementation instructions', 'Umsetzungsanweisungen', 'Upute za izvedbu')}</h6>
								</div>
								<div className="card-body">
									<ol className="mb-0" style={{ lineHeight: 1.8 }}>
										{displayedPlan.instructions.map((s: string, i: number) => (
											<li key={i} className="mb-2">{s}</li>
										))}
									</ol>
								</div>
							</div>
						)}

						{displayedPlan?.tools && Array.isArray(displayedPlan.tools) && displayedPlan.tools.length > 0 && (
							<div className="card mb-4">
								<div className="card-header bg-transparent border-success border-bottom">
									<h6 className="mb-0"><i className="bi bi-tools me-2"></i>{copy('Required tools & materials', 'Benötigte Werkzeuge und Materialien', 'Potrebni alati i materijali')}</h6>
								</div>
								<div className="card-body">
									{typeof displayedPlan.tools[0] === 'string' ? (
										<ul className="list-unstyled mb-0 small">
											{displayedPlan.tools.map((t: string, i: number) => (
												<li key={i} className="mb-2">
													<i className="bi bi-check-circle text-success me-2"></i>
													{t}
												</li>
											))}
										</ul>
									) : (
										<div className="small">
											{displayedPlan.tools.map((t: any, i: number) => (
												<div key={i} className="mb-3 pb-3 border-bottom border-light">
													<div className="fw-semibold mb-1">
														<i className="bi bi-tools text-primary me-2"></i>
														{t?.name ?? t}
													</div>
													{t?.description && <div className="text-muted ms-4">{t.description}</div>}
												</div>
											))}
										</div>
									)}
								</div>
							</div>
						)}

						{planPricing && (planPricing.mid != null || planPricing.min != null) && (
							<div className="card mb-4">
								<div className="card-header bg-transparent border-success border-bottom">
									<h6 className="mb-0">{copy('Estimated pricing (plan)', 'Geschätzte Kosten (Plan)', 'Procijenjena cijena (plan)')}</h6>
								</div>
								<div className="card-body">
									<div className="mb-2 d-flex justify-content-between">
										<span className="text-muted">{copy('Minimum', 'Minimum', 'Minimum')}</span>
										<span className="fw-bold">{Number(planPricing.min ?? 0).toLocaleString()} €</span>
									</div>
									<div className="mb-2 d-flex justify-content-between">
										<span className="text-muted">{copy('Mid-range', 'Mittlerer Bereich', 'Srednji raspon')}</span>
										<span className="fw-bold text-success">{Number(planPricing.mid ?? 0).toLocaleString()} €</span>
									</div>
									<div className="mb-0 d-flex justify-content-between">
										<span className="text-muted">{copy('Maximum', 'Maximum', 'Maksimum')}</span>
										<span className="fw-bold">{Number(planPricing.max ?? 0).toLocaleString()} €</span>
									</div>
									{planPricing.materials?.length > 0 && (
										<>
											<hr />
											<div className="small text-muted mb-2">{copy('Materials', 'Materialien', 'Materijali')}</div>
											{planPricing.materials.map((m: any, idx: number) => (
												<div key={idx} className="d-flex justify-content-between mb-2">
													<span>{m.name}</span>
													<span className="fw-bold">{Number(m.total ?? 0).toLocaleString()} €</span>
												</div>
											))}
										</>
									)}
									{planPricing.labor?.length > 0 && (
										<>
											<hr />
											<div className="small text-muted mb-2">{copy('Labor', 'Arbeitsleistung', 'Rad')}</div>
											{planPricing.labor.map((l: any, idx: number) => (
												<div key={idx} className="d-flex justify-content-between mb-1">
													<span>{l.name}</span>
													<span>{Number(l.total ?? 0).toLocaleString()} €</span>
												</div>
											))}
										</>
									)}
								</div>
							</div>
						)}
					</div>
				)}

				{/* Offer summary */}
				{seekerTab === 'plan' && offer && (
					<div className="col-12">
						<div className="card shadow-sm">
							<div className="card-header bg-transparent border-success border-bottom">
								<h6 className="mb-0">{copy('Accepted offer', 'Akzeptiertes Angebot', 'Prihvaćena ponuda')}</h6>
							</div>
							<div className="card-body">
								{(offer.proposal_text || offer.proposal_summary) && (
									<div className="mb-3">
										<div className="small text-muted text-uppercase mb-1">{copy('Proposal', 'Angebot', 'Ponuda')}</div>
										<div className="text-break">{offer.proposal_text || offer.proposal_summary}</div>
									</div>
								)}
								<div className="row g-3">
									{offer.estimated_timeline && (
										<div className="col-12 col-md-6">
											<div className="small text-muted text-uppercase mb-1">{copy('Timeline', 'Zeitplan', 'Vremenski plan')}</div>
											<div>{offer.estimated_timeline}</div>
										</div>
									)}
									{(() => {
										const estimated = offer.price_final ?? offer.price_mid ?? offer.price_min;
										return estimated != null ? (
											<div className="col-12 col-md-6">
												<div className="small text-muted text-uppercase mb-1">{copy('Estimated price', 'Geschätzter Preis', 'Procijenjena cijena')}</div>
												<div className="fw-semibold">{estimated.toLocaleString()} €</div>
											</div>
										) : null;
									})()}
									{offer.valid_until && (
										<div className="col-12 col-md-6">
											<div className="small text-muted text-uppercase mb-1">{copy('Offer valid until', 'Angebot gültig bis', 'Ponuda vrijedi do')}</div>
											<div>{new Date(offer.valid_until).toLocaleDateString('en-GB')}</div>
										</div>
									)}
									{offer.vat_rate != null && (
										<div className="col-12 col-md-6">
											<div className="small text-muted text-uppercase mb-1">{copy('VAT', 'MwSt.', 'PDV')}</div>
											<div>{Number(offer.vat_rate)}%</div>
										</div>
									)}
									{offer.public_notes && (
										<div className="col-12">
											<div className="small text-muted text-uppercase mb-1">{copy('Notes', 'Notizen', 'Bilješke')}</div>
											<div className="text-break">{offer.public_notes}</div>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				)}

				{/* Client Inputs */}
				{seekerTab === 'questionnaire' && plan && (
					<div className="col-12">
						<div className="card shadow-sm">
							<div className="card-header bg-transparent border-success border-bottom">
								<h6 className="mb-0">{copy('Client Inputs', 'Kundeneingaben', 'Unos klijenta')}</h6>
							</div>
							<div className="card-body">
								<div className="row g-3">
									{plan.garden_size != null && (
										<div className="col-12 col-md-4">
											<div className="text-muted small mb-1">{copy('Garden size', 'Gartengröße', 'Veličina vrta')}</div>
											<div className="fw-semibold">{Number(plan.garden_size)} m²</div>
										</div>
									)}
									{plan.garden_shape && (
										<div className="col-12 col-md-4">
											<div className="text-muted small mb-1">{copy('Garden shape', 'Gartenform', 'Oblik vrta')}</div>
											<div className="fw-semibold">{plan.garden_shape}</div>
										</div>
									)}
									{plan.location && (
										<div className="col-12 col-md-4">
											<div className="text-muted small mb-1">{copy('Location', 'Standort', 'Lokacija')}</div>
											<div className="fw-semibold">{plan.location}</div>
										</div>
									)}

									{(() => {
										const raw = (plan as any).garden_components;
										let components: string[] = [];
										if (Array.isArray(raw)) {
											components = raw.map(String);
										} else if (typeof raw === 'string') {
											try {
												const parsed = JSON.parse(raw);
												components = Array.isArray(parsed) ? parsed.map(String) : [];
											} catch {
												components = [];
											}
										}

										if (components.length === 0) return null;

										return (
											<div className="col-12">
												<div className="text-muted small mb-1">{copy('Garden components', 'Gartenkomponenten', 'Komponente vrta')}</div>
												<div className="d-flex flex-wrap gap-2">
													{components.map((componentId) => (
														<span key={String(componentId)} className="badge bg-success text-white">
															<i className="bi bi-check-circle me-1" />
															{getComponentLabel(String(componentId), language)}
														</span>
													))}
												</div>
											</div>
										);
									})()}

									{plan.needs_wants && plan.needs_wants.trim().length > 0 && (
										<div className="col-12">
											<div className="text-muted small mb-1">{copy('Needs & wants', 'Bedürfnisse & Wünsche', 'Potrebe i želje')}</div>
											<div className="text-break">{plan.needs_wants}</div>
										</div>
									)}

									{plan.maintenance_level && (
										<div className="col-12 col-md-4">
											<div className="text-muted small mb-1">{copy('Maintenance', 'Pflegeaufwand', 'Održavanje')}</div>
											<div className="fw-semibold text-capitalize">{plan.maintenance_level}</div>
										</div>
									)}

									{plan.plant_density_preference && (
										<div className="col-12 col-md-4">
											<div className="text-muted small mb-1">{copy('Plant density', 'Pflanzdichte', 'Gustoća sadnje')}</div>
											<div className="fw-semibold text-capitalize">{plan.plant_density_preference}</div>
										</div>
									)}

									{(() => {
										const raw = (plan as any).garden_uses;
										let uses: string[] = [];
										if (Array.isArray(raw)) uses = raw.map(String);
										else if (typeof raw === 'string') {
											try {
												const parsed = JSON.parse(raw);
												uses = Array.isArray(parsed) ? parsed.map(String) : [];
											} catch {
												uses = [];
											}
										}
										if (uses.length === 0) return null;
										const labelMap: Record<string, string> = {
											relaxing: copy('Relaxing / quiet time', 'Entspannung / Ruhezeit', 'Opuštanje / mir'),
											growing_food: copy('Growing food', 'Lebensmittel anbauen', 'Uzgoj hrane'),
											kids_pets: copy('Kids & pets', 'Kinder & Haustiere', 'Djeca i kućni ljubimci'),
											hosting: copy('Hosting', 'Gäste empfangen', 'Druženje'),
											dining_outdoors: copy('Outdoor dining', 'Essen im Freien', 'Blagovanje na otvorenom')
										};
										return (
											<div className="col-12">
												<div className="text-muted small mb-1">{copy('How you’ll use the garden', 'Wie Sie den Garten nutzen', 'Kako ćete koristiti vrt')}</div>
												<div className="d-flex flex-wrap gap-2">
													{uses.map((useKey) => (
														<span key={useKey} className="badge bg-secondary text-white">
															{labelMap[useKey] ?? useKey}
														</span>
													))}
												</div>
											</div>
										);
									})()}

									{plan.hosting_max_people != null && plan.hosting_max_people > 0 && (
										<div className="col-12 col-md-4">
											<div className="text-muted small mb-1">{copy('Hosting (max people)', 'Gäste empfangen (max. Personen)', 'Druženje (maks. osoba)')}</div>
											<div className="fw-semibold">{plan.hosting_max_people}</div>
										</div>
									)}

									{plan.children_pets && plan.children_pets !== 'none' && (
										<div className="col-12 col-md-4">
											<div className="text-muted small mb-1">{copy('Kids/pets priority', 'Priorität Kinder/Haustiere', 'Prioritet djece/ljubimaca')}</div>
											<div className="fw-semibold text-capitalize">{plan.children_pets}</div>
										</div>
									)}

									{plan.kids_count != null && plan.kids_count > 0 && (
										<div className="col-12 col-md-4">
											<div className="text-muted small mb-1">{copy('Kids count', 'Anzahl Kinder', 'Broj djece')}</div>
											<div className="fw-semibold">{plan.kids_count}</div>
										</div>
									)}

									{plan.kids_ages && plan.kids_ages.trim().length > 0 && (
										<div className="col-12 col-md-4">
											<div className="text-muted small mb-1">{copy('Kids ages', 'Alter der Kinder', 'Dob djece')}</div>
											<div className="fw-semibold">{plan.kids_ages}</div>
										</div>
									)}

									{plan.pets_description && plan.pets_description.trim().length > 0 && (
										<div className="col-12">
											<div className="text-muted small mb-1">{copy('Pets note', 'Hinweis zu Haustieren', 'Napomena o ljubimcima')}</div>
											<div className="text-break">{plan.pets_description}</div>
										</div>
									)}

									{plan.budget_min != null && plan.budget_max != null && (
										<div className="col-12">
											<div className="text-muted small mb-1">{copy('Budget range', 'Budgetspanne', 'Raspon budžeta')}</div>
											<div className="fw-semibold">
												€{Number(plan.budget_min).toLocaleString()} - €{Number(plan.budget_max).toLocaleString()}
											</div>
										</div>
									)}

									{plan.timeline && plan.timeline.trim().length > 0 && (
										<div className="col-12">
											<div className="text-muted small mb-1">{copy('Timeline', 'Zeitplan', 'Vremenski plan')}</div>
											<div className="fw-semibold">{plan.timeline}</div>
										</div>
									)}

									{plan.project_start_date && (
										<div className="col-12">
											<div className="text-muted small mb-1">{copy('Preferred start date', 'Bevorzugtes Startdatum', 'Željeni datum početka')}</div>
											<div className="fw-semibold">
												{new Date(plan.project_start_date).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })}
											</div>
										</div>
									)}
								</div>

								{/* Smart Q&A (collapsed) */}
								<div className="mt-4">
									<div className="text-muted small mb-2">{copy('Smart questions', 'Intelligente Fragen', 'Pametna pitanja')}</div>
									{seekerSmartQuestionRows.length > 0 ? (
										<details>
											<summary style={{ cursor: 'pointer', fontWeight: 600 }}>
												{copy('Show answered Smart Questions', 'Beantwortete Smart Questions anzeigen', 'Prikaži odgovorena pametna pitanja')}
											</summary>
											<div className="mt-3">
												{seekerSmartQuestionRows.map((row) => (
													<div key={row.idx} className="mb-3 pb-3 border-bottom">
														<div className="fw-semibold mb-1">{row.questionText}</div>
														{row.answerText && row.answerText.trim().length > 0 && (
															<div className="small text-muted">{copy('Answer', 'Antwort', 'Odgovor')}: {row.answerText}</div>
														)}
														{row.commentText && (
															<div className="small mt-1">
																<i className="bi bi-chat-left-text me-1" />
																<strong>{copy('Your note', 'Ihre Notiz', 'Vaša napomena')}:</strong> <span className="text-break">{row.commentText}</span>
															</div>
														)}
													</div>
												))}
											</div>
										</details>
									) : (
										<p className="text-muted mb-0">{copy('No Smart Questions were answered.', 'Es wurden keine Smart Questions beantwortet.', 'Nijedno pametno pitanje nije odgovoreno.')}</p>
									)}
								</div>
							</div>
						</div>
					</div>
				)}

				{/* Project Timeline – summary + task cards (seeker-friendly view) */}
				{seekerTab === 'timeline' && hasTimeline && (
					<div className="col-12">
						<div className="card border shadow-sm overflow-hidden">
							<div className="card-header bg-transparent border-success border-bottom">
								<h6 className="mb-0 text-success fw-semibold">
									<i className="bi bi-calendar-range me-2"></i>{copy('Project Timeline', 'Projektzeitplan', 'Vremenski plan projekta')}
								</h6>
								<p className="small text-muted mb-0 mt-1">{copy('Schedule and task progress for this project.', 'Zeitplan und Aufgabenfortschritt für dieses Projekt.', 'Raspored i napredak zadataka za ovaj projekt.')}</p>
							</div>
							<div className="card-body p-4">
								{(() => {
									const { startDate, endDate, totalDuration, gardenerCount } = timelineSummary;
									const start = new Date(startDate);
									const end = new Date(endDate);
									const formatDuration = (days: number): string => {
										if (days <= 0) return '—';
										if (days < 7) return copy(`${days} working day${days !== 1 ? 's' : ''}`, `${days} Arbeitstag${days !== 1 ? 'e' : ''}`, `${days} radni dan${days !== 1 ? 'a' : ''}`);
										const weeks = Math.floor(days / 5);
										if (weeks <= 1) return copy(`${days} working days`, `${days} Arbeitstage`, `${days} radnih dana`);
										return copy(
											`${days} working days (${weeks} week${weeks !== 1 ? 's' : ''})`,
											`${days} Arbeitstage (${weeks} Woche${weeks !== 1 ? 'n' : ''})`,
											`${days} radnih dana (${weeks} tjedan${weeks !== 1 ? 'a' : ''})`
										);
									};
									return (
										<div className="row g-3 mb-4">
											<div className="col-6 col-md-3">
												<div className="card border-0 bg-light">
													<div className="card-body p-2 text-center">
														<div className="small text-muted">{copy('Duration', 'Dauer', 'Trajanje')}</div>
														<div className="fw-bold">{formatDuration(totalDuration)}</div>
													</div>
												</div>
											</div>
											<div className="col-6 col-md-3">
												<div className="card border-0 bg-light">
													<div className="card-body p-2 text-center">
														<div className="small text-muted">{copy('Gardeners', 'Gärtner', 'Vrtlari')}</div>
														<div className="fw-bold">{gardenerCount}</div>
													</div>
												</div>
											</div>
											<div className="col-6 col-md-3">
												<div className="card border-0 bg-light">
													<div className="card-body p-2 text-center">
														<div className="small text-muted">{copy('Start', 'Start', 'Početak')}</div>
														<div className="fw-bold small">
															{start.toLocaleDateString(locale, { day: 'numeric', month: 'short' })}
														</div>
													</div>
												</div>
											</div>
											<div className="col-6 col-md-3">
												<div className="card border-0 bg-light">
													<div className="card-body p-2 text-center">
														<div className="small text-muted">{copy('Completion', 'Abschluss', 'Završetak')}</div>
														<div className="fw-bold small">
															{end.toLocaleDateString(locale, { day: 'numeric', month: 'short' })}
														</div>
													</div>
												</div>
											</div>
										</div>
									);
								})()}
								<div className="d-flex justify-content-between align-items-center mb-2">
									<span className="small fw-medium text-muted">{copy('Overall completion', 'Gesamtfortschritt', 'Ukupni napredak')}</span>
									<span className="small fw-bold text-success">{progressPct}%</span>
								</div>
								<div className="progress rounded-pill mb-4" style={{ height: 14 }}>
									<div
										className="progress-bar bg-success"
										role="progressbar"
										style={{ width: `${progressPct}%` }}
										aria-valuenow={progressPct}
										aria-valuemin={0}
										aria-valuemax={100}
									/>
								</div>
								<h6 className="mb-2 small text-muted text-uppercase">{copy('Tasks', 'Aufgaben', 'Zadaci')}</h6>
								<div className="row g-3">
									{normalizedTasks.map((task) => {
										const done = (task.progress ?? 0) >= 100 || task.status === 'completed';
										const inProgress = task.status === 'in_progress' || ((task.progress ?? 0) > 0 && !done);
										const statusLabel = done
											? copy('Completed', 'Abgeschlossen', 'Završeno')
											: inProgress
												? copy('In progress', 'In Bearbeitung', 'U tijeku')
												: copy('Not started', 'Nicht gestartet', 'Nije započeto');
										const statusBadge = done ? 'bg-success' : inProgress ? 'bg-warning' : 'bg-secondary';
										const startLabel = task.startDate
											? new Date(task.startDate).toLocaleDateString('en-GB', {
													day: 'numeric',
													month: 'short'
												})
											: null;
										const endLabel = task.endDate
											? new Date(task.endDate).toLocaleDateString('en-GB', {
													day: 'numeric',
													month: 'short'
												})
											: null;
										const dateRangeLabel =
											startLabel && endLabel ? `${startLabel} – ${endLabel}` : startLabel || endLabel || '';
										const progressValue = task.progress ?? 0;
										return (
											<div key={task.id} className="col-12 col-sm-6 col-lg-4">
												<button
													type="button"
													className="card border-0 bg-white shadow-sm h-100 text-start w-100 hover-shadow-sm"
													style={{
														borderLeft: `4px solid ${
															done ? '#198754' : inProgress ? '#ffc107' : '#6c757d'
														}`,
														cursor: 'pointer'
													}}
													onClick={() => setSelectedTaskForModal({ task, project })}
												>
													<div className="card-body p-3 d-flex flex-column gap-2">
														<div className="d-flex justify-content-between align-items-center gap-2">
															<span className={`badge ${statusBadge} text-uppercase small`}>
																{statusLabel}
															</span>
															{dateRangeLabel && (
																<span className="small text-muted">{dateRangeLabel}</span>
															)}
														</div>
														<div className="fw-semibold text-dark" style={{ lineHeight: 1.3 }}>
															{task.name}
														</div>
														<div className="d-flex align-items-center justify-content-between gap-2 mt-1">
															<div className="flex-grow-1">
																<div className="progress" style={{ height: 6 }}>
																	<div
																		className={`progress-bar ${
																			done
																				? 'bg-success'
																				: inProgress
																					? 'bg-success'
																					: 'bg-secondary'
																		}`}
																		role="progressbar"
																		style={{ width: `${progressValue}%` }}
																		aria-valuenow={progressValue}
																		aria-valuemin={0}
																		aria-valuemax={100}
																	/>
																</div>
															</div>
															<span className="small text-muted ms-2">{progressValue}%</span>
														</div>
														<div className="d-flex justify-content-between align-items-center mt-1">
															<span className="small text-muted">
																{task.duration ?? 0} day{(task.duration ?? 0) === 1 ? '' : 's'}
															</span>
															<span className="small text-muted">
																{done
																	? copy('Completed', 'Abgeschlossen', 'Završeno')
																	: inProgress
																		? copy('In progress', 'In Bearbeitung', 'U tijeku')
																		: copy('Upcoming', 'Bevorstehend', 'Nadolazeće')}
															</span>
														</div>
													</div>
												</button>
											</div>
										);
									})}
								</div>
							</div>
						</div>
					</div>
				)}

			</div>

			{/* Task Detail Modal – seekers can open tasks to view details and add notes/attachments */}
			{selectedTaskForModal && (
				<TaskDetailModal
					task={selectedTaskForModal.task}
					project={selectedTaskForModal.project}
					gardeners={[]}
					onClose={() => setSelectedTaskForModal(null)}
					onUpdate={() => {
						// For seeker view, notes and attachments don't affect the outer timeline,
						// so we can simply close or leave as-is. Re-fetching is not required here.
					}}
				/>
			)}

			{/* Image modal for before/after images */}
			{imageModalOpen && (
				<ImageModal
					isOpen={imageModalOpen}
					onClose={() => setImageModalOpen(false)}
					images={[
						{
							url: beforeImageUrl || placeholderImage,
							alt: 'Before',
							label: 'Before'
						},
						{
							url: afterImageUrl || beforeImageUrl || placeholderImage,
							alt: 'After (proposed)',
							label: 'After (proposed)'
						}
					].filter((img, index, arr) => {
						// Avoid duplicate images if before/after are the same
						const firstIndex = arr.findIndex(other => other.url === img.url && other.label === img.label);
						return firstIndex === index;
					})}
					initialIndex={imageModalIndex}
				/>
			)}
		</div>
	);
}
